import { LegModel } from '@tajawal/web-flights-sdk/lib/types/leg.types';
import { getSearchResultsLegTestIds } from '@/utils/test-ids.utils';
import { COLOR_VARIANTS, Tooltip, Typography, TYPOGRAPHY_VARIANT, VisualIcons } from '@tajawal/ct-web-design-system';
import { useTranslation } from 'react-i18next';
import { useService } from '@/contexts/ServiceContext';
import Styled from './LegInfo.styled';
import { getAirlineOperatedTooltipText, getLogoImg } from '@/utils/content.utils';
import Time from '../FlightResultCard/components/Time/Time';
import Stops from '@/app-desktop/components/Stops';
import Airport from '../FlightResultCard/components/Airports/Airport';
import AirlineLogo from '@/app-common/components/AirlineLogo/AirlineLogo';
import _map from 'lodash/map';
import React from 'react';
import BaggageTooltip from '@/app-desktop/components/BaggageTooltip';
import _last from 'lodash/last';
import { BaggageInfoModel, LegBaggageInfo } from '@tajawal/web-flights-sdk/lib/types/baggage.types';
import { buildTestId } from '@/utils/helpers.utils';
import _size from 'lodash/size';

export type LegInfoProps = {
  leg: LegModel;
  baggageIcon: string;
  baggageInfo: LegBaggageInfo[];
  index: number;
  hasFareFamilies: boolean;
  testIds: ReturnType<typeof getSearchResultsLegTestIds>;
};

const IconMap = {
  BaggageIncluded: VisualIcons.BaggageIncluded,
  BaggagePlus: VisualIcons.BaggagePlus,
  BaggageNotIncluded: VisualIcons.BaggageNotIncluded,
};

function LegInfo({ leg, baggageIcon, index, testIds, baggageInfo, hasFareFamilies }: LegInfoProps): JSX.Element {
  const { t } = useTranslation();
  const { resources } = useService();

  const { getAirportType, getAirlineLabel } = resources;

  const {
    ticketSoldBy,
    stopCount,
    departure,
    arrivalFlag,
    arrival,
    arrivalTime,
    isNearbyOrigin,
    durationText,
    isNearbyDestination,
    destinationId,
    destinationAircraft,
    legSummary,
    segments,
    segmentsWithTechnicalStops,
  } = leg || {};
  const Icon = IconMap[baggageIcon];
  const freeBaggage = baggageInfo?.[index]?.freeBaggage;
  const baggage = freeBaggage || _last(segments)?.additionalDetails?.freeBaggage;

  const cabinBaggage = baggage?.cabin as BaggageInfoModel;
  const checkedBaggage = baggage?.checkIn as BaggageInfoModel;
  const underSeatBaggage = baggage?.underSeatBaggage as BaggageInfoModel;
  const baggageOptionsAvailable = baggage?.baggageOptionsAvailable;
  const baggageOfferText = baggageOptionsAvailable ? t('Baggage.Extra_Baggage_Next_Step') : '';

  const logos = _map(legSummary?.legCarriers, (carrier) => getLogoImg(carrier));
  const airlineOperatedByText = getAirlineOperatedTooltipText(legSummary?.legCarriers, resources);
  const options =
    _size(logos) > 1
      ? {
          title: (
            <Typography align="center" variant={TYPOGRAPHY_VARIANT.BODY2} component="p" color={COLOR_VARIANTS.WHITE}>
              {t('SearchResults.AirlinesPartiallyOperated', { flights: airlineOperatedByText })}
            </Typography>
          ),
        }
      : {};
  const Wrapper = _size(logos) > 1 ? Tooltip : Styled.Wrapper;
  const titles = _map(legSummary?.legCarriers, (carrier) => getAirlineLabel(carrier)).join(', ');

  return (
    <Styled.Wrapper id={leg.id} data-testid={testIds.legTestId}>
      <Styled.Header>
        {/* @ts-ignore wrapper for tooltip needs title of component */}
        <Wrapper {...options} placement="top">
          <Styled.HeaderWrapper $multipleAirlines={legSummary?.legCarriers.length > 1}>
            <AirlineLogo
              hasTooltip={false}
              logos={logos}
              testId={testIds.airlineLogoTestId}
              operatedByAirlinesText={airlineOperatedByText}
            />
            <Styled.Title $multipleAirlines={legSummary?.legCarriers.length > 1}>
              <Typography component="span" variant={TYPOGRAPHY_VARIANT.CAPTIONSEMBLD}>
                {titles}
              </Typography>
            </Styled.Title>
          </Styled.HeaderWrapper>
        </Wrapper>
        <Styled.LegInfo>
          <Styled.TimeStopsWrapper>
            <Time testId={testIds.depTimeTestId} isDeparture time={departure} />
            <Stops
              segmentStops={segmentsWithTechnicalStops}
              testId={testIds.stopsLabelTestId}
              stopsLength={stopCount}
              hasChangeAirportFlag={leg.hasChangeAirportFlag}
              resources={resources}
            />
            <Time
              testId={testIds.arTimeTestId}
              time={arrival}
              arrivalDate={new Date(arrivalTime)}
              arrivalFlag={arrivalFlag}
            />
          </Styled.TimeStopsWrapper>
          <Styled.AirportWrapper>
            <Airport
              testId={testIds.depAirportTestId}
              isNearbyAirport={isNearbyOrigin}
              name={leg.originId}
              type={getAirportType(leg.originId, leg.originAircraft)}
              resources={resources}
            />
            <Tooltip
              title={
                <Typography component="p" color={COLOR_VARIANTS.WHITE} variant={TYPOGRAPHY_VARIANT.BODY2}>
                  {t('Baggage.TotalDuration')}
                </Typography>
              }
              placement="bottom"
            >
              <Styled.Duration>
                <Typography
                  testId={testIds.durationLabelTestId}
                  component="span"
                  color={COLOR_VARIANTS.GREYCHATEAU}
                  variant={TYPOGRAPHY_VARIANT.CAPTIONREG}
                >
                  {durationText}
                </Typography>
              </Styled.Duration>
            </Tooltip>
            <Airport
              testId={testIds.arAirportTestId}
              isNearbyAirport={isNearbyDestination}
              name={destinationId}
              type={getAirportType(destinationId, destinationAircraft)}
              isReturnFlight
              resources={resources}
            />
          </Styled.AirportWrapper>
        </Styled.LegInfo>

        {baggageIcon && (
          <Tooltip
            title={
              <div>
                <BaggageTooltip
                  cabin={cabinBaggage}
                  checked={checkedBaggage}
                  underSeatBaggage={underSeatBaggage}
                  testIdPrefix={buildTestId('BaggageTooltip')}
                  baggageOfferText={baggageOfferText}
                  hasFareFamilies={hasFareFamilies}
                />
              </div>
            }
            placement="top"
          >
            <Styled.BaggageIcon>
              <Icon style={{ flexShrink: 0 }} data-testid={testIds.baggageIcon} fill="none" width="32" height="24" />
            </Styled.BaggageIcon>
          </Tooltip>
        )}
      </Styled.Header>

      {ticketSoldBy && (
        <Typography color={COLOR_VARIANTS.GREYCHATEAU} component="p" variant={TYPOGRAPHY_VARIANT.CAPTIONREG}>
          {`${t('FlightCard.Leg_SoldBy')} ${getAirlineLabel(ticketSoldBy)}`}
        </Typography>
      )}
    </Styled.Wrapper>
  );
}

export default LegInfo;
